/* eslint-disable ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies */
// Extends From
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
// Ember Imports
import { computed } from '@ember/object';
import { service } from '@ember/service';

export default class LoginFailedController extends Controller {
  // Attrs
  queryParams = ['error', 'error_description'];

  // Services
  @service
  environment;

  // Props
  @tracked error = null;

  // Methods
  // errorTitle returns a human friendly quick overall description of the error
  // that occurred.
  get errorTitle() {
    switch (this.error) {
      case 'request_unauthorized':
        return 'Not authorised';
      case 'request_forbidden':
        return 'Not allowed';
      case 'invalid_request':
        return 'Invalid request';
      case 'unauthorized_client':
        return 'Client unauthorised';
      case 'access_denied':
        return 'Access denied';
      case 'unsupported_response_type':
        return 'Unsupported request';
      case 'invalid_scope':
        return 'Invalid scope';
      case 'server_error':
        return 'Server error';
      case 'temporarily_unavailable':
        return 'Temporarily unavailable';
      case 'unsupported_grant_type':
        return 'Unsupported request';
      case 'invalid_grant':
        if (this.error_description === 'Token already expired') {
          return this.error_description;
        }
        return 'Invalid grant';
      case 'invalid_client':
        return 'Invalid client';
      case 'invalid_state':
        return 'Invalid state';
      case 'insufficient_entropy':
        return 'Insufficient entropy';
      case 'misconfiguration':
        return 'Internal error';
      case 'not_found':
        return 'Not found';
      case 'invalid_token':
        return 'Invalid token format';
      case 'token_signature_mismatch':
        return 'Token signature mismatch';
      case 'token_expired':
        return 'Token expired';
      case 'scope_not_granted':
        return 'Not authorised';
      case 'token_claim':
        return 'Invalid token';
      case 'token_inactive':
        return 'Inactive token';
    }

    return 'Unknown error';
  }

  // errorMessage returns a human friendly fuller description and explanation
  // of the error that occurred.
  get errorMessage() {
    switch (this.error) {
      case 'request_unauthorized':
        return 'The request could not be authorized.';
      case 'request_forbidden':
        return 'The request is not allowed.';
      case 'invalid_request':
        return 'The request is missing a required parameter, includes an invalid parameter value, includes a parameter more than once, or is otherwise malformed.';
      case 'unauthorized_client':
        return 'The client is not authorized to request a token using this method.';
      case 'access_denied':
        return 'The resource owner or authorization server denied the request.';
      case 'unsupported_response_type':
        return 'The authorization server does not support obtaining a token using this method.';
      case 'invalid_scope':
        return 'The requested scope is invalid, unknown, or malformed.';
      case 'server_error':
        return 'The authorization server encountered an unexpected condition that prevented it from fulfilling the request.';
      case 'temporarily_unavailable':
        return 'The authorization server is currently unable to handle the request due to a temporary overloading or maintenance of the server.';
      case 'unsupported_grant_type':
        return 'The authorization grant type is not supported by the authorization server.';
      case 'invalid_grant':
        if (this.error_description === 'Token already expired') {
          return "The access token issued to you has already expired.  This is most likely caused by your device's clock being set incorrectly.";
        }
        return 'The provided authorization grant (e.g., authorization code, resource owner credentials) or refresh token is invalid, expired, revoked, does not match the redirection URI used in the authorization request, or was issued to another client.';
      case 'invalid_client':
        return 'Client authentication failed (e.g., unknown client, no client authentication included, or unsupported authentication method).';
      case 'invalid_state':
        return 'The state is missing or has less than the required amount of characters and is therefore considered too weak.';
      case 'insufficient_entropy':
        return 'The request used a security parameter (e.g., anti-replay, anti-csrf) with insufficient entropy.';
      case 'misconfiguration':
        return 'The request failed because of an internal error that is probably caused by misconfiguration.';
      case 'not_found':
        return 'Could not find the requested resource(s).';
      case 'invalid_token':
        return 'Invalid token format.';
      case 'token_signature_mismatch':
        return 'Token signature mismatch.';
      case 'token_expired':
        return 'Token expired.';
      case 'scope_not_granted':
        return 'The token was not granted the requested scope.';
      case 'token_claim':
        return 'The token failed validation due to a claim mismatch.';
      case 'token_inactive':
        return 'Token is inactive because it is malformed, expired or otherwise invalid.';
      case 'session_timeout':
        return 'Your session has expired, possibly due to a problem with your internet connection.';
    }

    return 'An unknown error has occurred.';
  }

  // gatekeeperUri returns the uri to send users to sign in.
  @computed('environment.name')
  get gatekeeperUri() {
    return `https://${this.environment.uriPrefix}go.linc-ed.com`;
  }
}
